import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm , useFieldArray } from 'react-hook-form';
import { Layout, ResetConfirmationModal } from '../../../../components';
import AppContext from '../../../../contexts/AppContext';
import { handleApi,  capitalize, AudioSpinner,useToast, SearchableDropdown } from '../../../../utils/utils';

const NewUser = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { initialUser } = location.state || {};
  const initialFormState = {
    employee_id: '',
    role_id:'',
    username: '',
  }
  const [user, setUser] = useState(initialFormState);
  const [roles, setRoles] = useState([]);
  const [formattedRoles, setFormattedRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formattedEmployees, setFormattedEmployees] = useState([]);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null); //state to hold employee details
  const [selectedRoleDetails, setSelectedRoleDetails] = useState(null); //state to hold employee details
  const { userData, currentColor, actionCount,setActionCount} = useContext(AppContext);
  console.log('initialUser',initialUser)
  const { showToast, toastMessage } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false); // State for the reset confirmation modal
  const [focusedIndex, setFocusedIndex] = useState(null);
  const userRef = useRef(null); // Ref to the user content for PDF generation
  const quantityRefs = useRef([]);    // a ref for each quantity input field to manage focus

  const { register, control, watch, handleSubmit, reset, setValue, getValues } = useForm({
    defaultValues: {
      employee_id: '',
      role_id:'',
      username: '',
      password: '',
    }
  });
  
  const watchedEmployeeId = watch('employee_id'); // Watch for changes on the 'employee_id' field
  // New, reusable function to fetch data. It now accepts `showToast` as a parameter.
  const fetchData = async (apiEndpoint, showToast) => {
    const apiMethod = 'GET';
    const cleanedData = {};
    const parameters = {};

    try {
      const response = await handleApi(apiEndpoint, cleanedData, userData, apiMethod, parameters);
      if (response.error) {
        console.error(`Error fetching data from ${apiEndpoint}: ${response.errorMessage}`);
        showToast(`Error: ${response.errorMessage}`, 'error'); // Use showToast here
        return null; // Return null on error
      }
      const { status, data } = response;
      if (status === 200) {return data; // Return the data on success
      } else {
        showToast(`Error ${status}: ${response.statusText}`, 'error'); // Use showToast here
        return null;
      }
    } catch (error) {
      showToast(error.message || `An unexpected error occurred while fetching ${apiEndpoint}.`, 'error'); // Use showToast here
      return null;
    }
  };
 
  useEffect(() => {
    const loadInitialData = async () => {// Fetch employees
      const employeesData = await fetchData('employees', showToast);
      if (employeesData) {
        setEmployees(employeesData);
        setFormattedEmployees(employeesData.map(c => ({
          value: c.id,
           label: `${c.first_name} ${c.last_name}`
        })));
      }
 
      // Fetch roles
      const rolesData = await fetchData('roles', showToast);
      console.log('rolesData',rolesData)
      if (rolesData) {
        setRoles(rolesData);
        // Filter out null/undefined values before mapping
        setFormattedRoles(rolesData.filter(r => r).map(r => ({
          value: r.id,
          label: r.name || '',
        })));
      }
    };
    loadInitialData();
  }, []);



  // Use a useEffect hook to update the employee details whenever the watchedEmployeeId changes
  useEffect(() => {
    if (watchedEmployeeId) {
      const selectedEmployee = employees.find(c => c.id === parseInt(watchedEmployeeId));
      setSelectedEmployeeDetails(selectedEmployee || null);
    } else {setSelectedEmployeeDetails(null); }
  }, [watchedEmployeeId, employees]);


  const handleEmployeeSelection = (employee) => {
    setValue('employee_id', employee.value);
    setValue('username', employee.email);
    setSelectedEmployeeDetails(employee);
  };

  
   const handleRoleSelection = (role) => {setValue('role_id', role.value);};
  
  const handleAction = (action) => {
    if (action === 'download') { showToast(`Simulated action: ${action}`, 'success');
    } else if (action === 'print') {window.print();
    } else {showToast(`Simulated action: ${action}`, 'success');}
  };

  
  const handleReset = () => {setShowResetModal(true);};
  const confirmReset = () => {
    reset(initialFormState); // Reset form fields
    setUser(initialFormState); // Reset state for other components
    setSelectedEmployeeDetails(null); // Clear selected employee details
    setSelectedRoleDetails(null); // Reset the role dropdown state as well
    setShowResetModal(false); // Close the modal
    showToast('User form has been reset!', 'info');
  };

  const cancelReset = () => {setShowResetModal(false);};

  const handleSave = async () => {
    const formData = getValues();
    // Validate if employee, role, and username are selected
    if (!selectedEmployeeDetails) {
      showToast('Please select an employee.', 'error');
      return;
    }
    if (!selectedRoleDetails) {
      showToast('Please select a user role.', 'error');
      return;
    }
    if (!formData.username) {
      showToast('Login ID is required.', 'error');
      return;
    }

    setIsLoading(true);

    const dataToSave = {
      ...formData,
      username: selectedEmployeeDetails?.email || formData.username,
      // Use '12345' as the default password if the user does not enter one.
      password: `hashed(${formData.password || '12345'})`,
    };

    console.log('Final data to save:', dataToSave);
    const apiEndpoint = 'users';

    try {
      // FIX: Pass the correct apiEndpoint variable
      const response = await handleApi(apiEndpoint, dataToSave, userData, 'POST');
      if (response && response.status === 200) {
        showToast(response.data.message || 'User saved successfully!', 'success');
        // Reset the form and state variables after successful save
        reset(initialFormState);
        setSelectedEmployeeDetails(null);
        setSelectedRoleDetails(null);
      } else {
        showToast('Failed to save user.', 'error');
      }
    } catch (error) {
      showToast('An error occurred while saving the user.', 'error');
      console.error('Save error:', error);
    } finally {
      setIsLoading(false);
    }
  };




  return (
    <Layout>
    <div className="min-h-screen text-white p-2 sm:p-4">
      {toastMessage.show && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md z-50
                        ${toastMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {toastMessage.message}
        </div>
      )}

      <div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center">
            NEW USER
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

      <div className="bg-gray-600 rounded-3xl shadow-lg p-2 sm:p-4 mx-auto max-w-[50%]">
        

        {isLoading ? (
          <div className="flex flex-col items-center py-6">
            <AudioSpinner />
            <p className="mt-4 text-gray-400">Loading. Please wait...</p>
          </div>
        ) : (
          
          <div className="space-y-6">
          <div className="space-y-6">
            <div className="flex flex-col gap-6 w-full"> 
              {/* 💡 FIX: Added p-2 sm:p-4 back to this inner div to maintain spacing */}
              {/* You can use a hidden input field for username since its value is set programmatically */}
              <input type="hidden" {...register('username')} />

              {/* Hidden inputs to register employee_id and role_id with react-hook-form */}
              <input type="hidden" {...register('employee_id')} />
              <input type="hidden" {...register('role_id')} />

              <div className="bg-gray-500 px-4 py-4 rounded-xl text-cyan-200 w-full">
                <div className="flex flex-col gap-4 text-xs">
                  {/* Parent container for Select Employee */}
                  <div className="flex flex-col items-center">
                    <label className="font-medium text-cyan-200 mb-1">Employee Name</label>
                    <SearchableDropdown
                      items={formattedEmployees}
                      onSelect={handleEmployeeSelection}
                      selectedItem={selectedEmployeeDetails}
                      placeholder="Search employee..."
                    />
                  </div>

                  {/* Parent container for Select Role */}
                  <div className="flex flex-col items-center justify-center">
                    <label className="font-medium text-cyan-200 mb-1">User Role</label>
                    <SearchableDropdown
                      items={formattedRoles}
                      onSelect={handleRoleSelection}
                      selectedItem={selectedRoleDetails}
                      placeholder="Search role..."
                      inputClassName="text-center placeholder:text-center"
                    />
                  </div>


                  {/* Display section for selected employee details */}
                  {selectedEmployeeDetails && (
                <>
                  <div className="flex flex-col">
                    <label className="font-medium text-cyan-200 mb-1 text-center">Login Id</label>
                    <input
                      type="text"
                      className="text-center w-full bg-gray-500 text-white border-b-2 focus:outline-none border-cyan-200 rounded-md p-1 opacity-70 cursor-not-allowed"
                      value={selectedEmployeeDetails?.email || 'NA'}
                      readOnly
                      disabled
                    />
                  </div>
                </>
              )}
            </div>
          </div>     
        </div>
    </div>
  </div>
)}
  
  <div className="flex justify-between mt-6">
  <button
        type="button"
        // Add the onClick handler to call handleReset
        onClick={handleReset}
        className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300"
        >
        Cancel
            </button>
            
            <button
              onClick={handleSubmit(handleSave)}
              className="px-4 py-2 rounded-xl font-semibold text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-all duration-300"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
      </div>
     
    </div>
    {/* {showResetModal && ( */}
      <ResetConfirmationModal
      show={showResetModal}
      onConfirm={confirmReset}
      onCancel={cancelReset}
    />
    {/* )} */}
    </Layout>
  );
};

export default NewUser;